import Image from 'next/image';

import { Box, Flex, Heading, List, ListIcon, ListItem, Text } from '@chakra-ui/react';
import { ArrowRightIcon, CheckedIcon } from '@udacity/chakra-uds-icons';

import { ButtonLink } from '~/components/button-link';
import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';

// TODO: refactor this data into Sanity
const sellingPoints = [
  {
    title: 'The right **learning** product',
    glyphName: 'graduation-cap-two',
    points: [
      '**Time efficient:** Compact & flexible learning.',
      '**Relevant:** Content is in line with industry trends and enables practical application of necessary skills.',
      '**Cost efficient:** Good value for money.',
    ],
  },
  {
    title: 'The right **delivery** mechanism',
    glyphName: 'heart-path-two',
    points: [
      '**Targeted outreach:** Clear and tailored outreach to attract applicants in line with the program’s objectives.',
      '**Selection process:** Diligent application and filtration processes that are tailored to desired program outcomes.',
      '**Engagement:** Active learner and stakeholder engagement to ensure best-possible program outcomes.',
      '**Monitoring:** Continuous tracking of learner progress and feedback to ensure best-in-class end-to-end learner experience and outcomes.',
      '**Reporting:** Structured and steady reporting of program progress versus desired outcomes to all stakeholders involved and actioning necessary changes accordingly.',
    ],
  },
];

export function TalentTransformationSuiteSection({ onCtaClick }: { onCtaClick?: () => void }) {
  return (
    <Box
      as='section'
      backgroundColor='accent.sky.100'
      position='relative'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <Box
        display={{
          base: 'none',
          md: 'block',
        }}
      >
        <Image
          alt=''
          sizes='1440px'
          src='/images/backgrounds/blue-lines-wide.png'
          style={{
            objectFit: 'cover',
            objectPosition: '-80px bottom',
          }}
          fill
        />
      </Box>
      <ContentContainer position='relative'>
        <Flex
          gap='32px'
          flexDir={{
            base: 'column',
            md: 'row',
          }}
        >
          <Flex flexDir='column' gap='48px'>
            <Box
              flex='1'
              marginTop={{
                md: '68px',
              }}
            >
              <Heading as='h2' marginBottom='8px' size='marketing-5xl'>
                Udacity’s Talent Transformation Suite
              </Heading>
              <Text marginBottom='48px'>
                Combine technology training for employees with industry experts, mentors, and projects, for critical
                thinking that pushes innovation.
                <br />
                <br />
                Our proven upskilling system goes after success—relentlessly.
              </Text>
              <ButtonLink
                buttonProps={{
                  rightIcon: <ArrowRightIcon height='24px' width='24px' />,
                  size: 'icon-right',
                  width: {
                    base: '100%',
                    md: 'fit-content',
                  },
                  onClick: onCtaClick,
                }}
                linkProps={{
                  href: '/get-started/government',
                }}
              >
                Learn More
              </ButtonLink>
            </Box>
            <Box
              flex='1 1 312px'
              position='relative'
              display={{
                base: 'none',
                md: 'block',
              }}
            >
              <Image
                alt=''
                sizes='465px'
                src='/images/backgrounds/floating-shapes-2.png'
                style={{
                  objectFit: 'cover',
                  objectPosition: 'bottom',
                  transform: 'scale(-1, -1)',
                }}
                fill
              />
            </Box>
          </Flex>
          <Flex flexDir='column' gap='24px'>
            {sellingPoints.map((sellingPoint) => (
              <Card
                key={sellingPoint.title}
                as='article'
                border='none'
                borderRadius='lg'
                boxShadow='0px 2px 4px -1px rgba(11, 11, 11, 0.06), 0px 4px 6px -1px rgba(11, 11, 11, 0.10)'
                padding={{
                  base: '24px 16px',
                  md: '40px 32px',
                }}
              >
                <Flex
                  marginBottom='16px'
                  alignItems={{
                    md: 'center',
                  }}
                  flexDir={{
                    base: 'column-reverse',
                    md: 'row',
                  }}
                  gap={{
                    base: '16px',
                    md: 'unset',
                  }}
                >
                  <HeadingWithEmphasis
                    text={sellingPoint.title}
                    emphasisProps={{
                      color: 'blue.500',
                    }}
                    headingProps={{
                      size: 'marketing-2xl',
                      as: 'p',
                      marginRight: {
                        md: 'auto',
                      },
                    }}
                  />
                  <Glyph height={56} name={sellingPoint.glyphName} width={56} />
                </Flex>
                <List display='flex' flexDir='column' gap='8px'>
                  {sellingPoint.points.map((point) => (
                    <ListItem key={point} display='flex'>
                      <ListIcon as={CheckedIcon} color='blue.500' height='24px' width='24px' />
                      <HeadingWithEmphasis
                        text={point}
                        emphasisProps={{
                          fontWeight: '600',
                        }}
                        headingProps={{
                          as: 'p',
                          size: 'marketing-md',
                          fontFamily: 'body',
                          fontWeight: 'normal',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Card>
            ))}
          </Flex>
        </Flex>
      </ContentContainer>
    </Box>
  );
}
